import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Mini 24h spread volatility histogram + percentile indicator.
 *
 * Renders below the live spread bar in Dashboard hero. Pulls last 24h of spread
 * samples from /api/spread-history/{symbol}, buckets them, and highlights where
 * the current spread sits versus the range.
 */
export default function SpreadHistogram({ symbol = "ETHUSDT" }) {
  const { data } = useQuery({
    queryKey: ["spread-history", symbol, 24],
    queryFn: () => api.spreadHistory(symbol, 24),
    refetchInterval: 60000,
  });

  const buckets = useMemo(() => {
    if (!data?.samples?.length) return null;
    const pcts = data.samples.map((s) => s.spread_pct);
    const lo = Math.min(...pcts);
    const hi = Math.max(...pcts);
    if (hi === lo) return { bars: pcts.map(() => 1), lo, hi };
    const N = 32;
    const bars = Array(N).fill(0);
    pcts.forEach((p) => {
      const idx = Math.min(N - 1, Math.floor(((p - lo) / (hi - lo)) * N));
      bars[idx] += 1;
    });
    const max = Math.max(...bars);
    return { bars: bars.map((b) => b / max), lo, hi };
  }, [data]);

  if (!data || !data.stats) {
    return (
      <div className="dxd-hist-empty">
        <span className="dxd-eyebrow">24s spread aralığı toplanıyor…</span>
      </div>
    );
  }

  const { current, min, max, avg, percentile } = data.stats;
  const range = max - min;
  const currentPos = range > 0 ? ((current - min) / range) * 100 : 50;

  const tone =
    percentile >= 0.8 ? "hot" :
    percentile >= 0.55 ? "warm" :
    percentile <= 0.2 ? "cold" : "neutral";

  const verdict =
    tone === "hot"  ? `Spread normalin %${Math.round(percentile * 100)} üstünde — fırsat geniş` :
    tone === "warm" ? "Ortalamadan biraz üstünde" :
    tone === "cold" ? "Normalin altında — sıkı spread" :
    "Tipik aralık içinde";

  return (
    <div className={`dxd-hist tone-${tone}`} data-testid="spread-histogram">
      <div className="dxd-hist-bars">
        {buckets?.bars.map((h, i) => (
          <span key={i} className="dxd-hist-bar" style={{ height: `${Math.max(6, h * 100)}%` }} />
        ))}
        <span className="dxd-hist-current" style={{ left: `${currentPos}%` }} />
      </div>
      <div className="dxd-hist-foot">
        <span className="dxd-hist-vd">{verdict}</span>
        <span className="dxd-hist-stats">
          <em>Min</em> {min.toFixed(3)}%
          <em>Ort</em> {avg.toFixed(3)}%
          <em>Max</em> {max.toFixed(3)}%
        </span>
      </div>
    </div>
  );
}
